import type { PhotoModel } from '@sosocio/creator-types/database';
import type { PageObjectModel } from '@sosocio/creator-types/project';

export interface CalculateMaximumObjectSizeOptions {
	objectData: MaximumObjectSizeObjectData;
	offeringDpi: MaximumObjectSizeOfferingDpi;
	photoData?: MaximumObjectSizePhotoData;
	policy: MaximumObjectSizePolicy;
}

export interface MaximumObjectSize {
	croppedQualityHeight: number;
	croppedQualityWidth: number;
	cropHeight: number;
	cropWidth: number;
	height: number;
	qualityHeight: number;
	qualityWidth: number;
	width: number;
}

export type MaximumObjectSizeObjectData = (
	Pick<PageObjectModel, 'type'>
	& Partial<Pick<
		PageObjectModel,
		'cropheight' | 'cropwidth' | 'maxheight' | 'maxwidth'
	>>
);

export interface MaximumObjectSizeOfferingDpi {
	configDpi: number;
	minimumDpi: number;
	qualityDpi: number;
}

export type MaximumObjectSizePhotoData = {
	[Key in 'height' | 'width']: NonNullable<PhotoModel[Key]>;
};

export interface MaximumObjectSizePolicy {
	applyUpscaling: boolean;
	applyVectorization: boolean;
	maximumOutputPixels: number;
	recommendedMaximumScale: number;
}

/**
 * Calculates maximum and recommended page-object dimensions from explicit DPI
 * and enlargement policy without reading application configuration or state.
 *
 * Crop limits retain the source-photo crop ratio. Upscaling and vectorization
 * share the established enlargement limit because either policy permits the
 * source to exceed its original raster dimensions.
 *
 * @param options Object geometry, optional source-photo dimensions, offering DPI, and enlargement policy.
 * @returns Maximum and recommended full/cropped dimensions in page units.
 */
export function calculateMaximumObjectSize({
	objectData,
	offeringDpi,
	photoData,
	policy,
}: CalculateMaximumObjectSizeOptions): MaximumObjectSize {
	const minimumQualityScale = offeringDpi.configDpi / offeringDpi.minimumDpi;
	const recommendedQualityScale = offeringDpi.configDpi / offeringDpi.qualityDpi;
	let maxHeight = objectData.maxheight || 0;
	let maxWidth = objectData.maxwidth || 0;
	let maxCroppedHeight = maxHeight;
	let maxCroppedWidth = maxWidth;

	if (
		photoData
		&& objectData.cropwidth
		&& objectData.cropheight
	) {
		const photoHeightCropRatio = objectData.cropheight / photoData.height;
		const photoWidthCropRatio = objectData.cropwidth / photoData.width;

		maxCroppedHeight *= photoHeightCropRatio;
		maxCroppedWidth *= photoWidthCropRatio;
	}

	if (
		photoData
		&& (
			policy.applyUpscaling
			|| policy.applyVectorization
		)
	) {
		const upscaleFactor = Math.min(
			policy.recommendedMaximumScale,
			Math.sqrt(
				policy.maximumOutputPixels / (maxWidth * maxHeight),
			),
		);

		maxCroppedHeight *= upscaleFactor;
		maxCroppedWidth *= upscaleFactor;
		maxHeight *= upscaleFactor;
		maxWidth *= upscaleFactor;
	}

	return {
		croppedQualityHeight: maxCroppedHeight * recommendedQualityScale,
		croppedQualityWidth: maxCroppedWidth * recommendedQualityScale,
		cropHeight: maxCroppedHeight * minimumQualityScale,
		cropWidth: maxCroppedWidth * minimumQualityScale,
		height: maxHeight * minimumQualityScale,
		qualityHeight: maxHeight * recommendedQualityScale,
		qualityWidth: maxWidth * recommendedQualityScale,
		width: maxWidth * minimumQualityScale,
	};
}

export default calculateMaximumObjectSize;
